/**
 * Zenprexi Bio-Risk™ API Client
 */

import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { store } from '@/store';
import {
  normalizeAlert,
  normalizeCARIHistory,
  normalizeCARIScore,
  normalizeCredentialOps,
  normalizeDepartment,
  normalizeFleetDashboard,
  normalizeFleetSummary,
  normalizeFleetWorkerStatus,
  normalizePilotReadiness,
  normalizeUser,
  normalizeWorker,
} from '@/utils/normalize';
import { UserRole } from '@/types';
import { logout, setTokens } from '@/store/authSlice';
import { assertSecureApiTransport } from '@/utils/transport';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v2';
const API_KEY = import.meta.env.VITE_API_KEY;

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    ...(API_KEY ? { 'X-API-Key': API_KEY } : {}),
  },
});

// Request interceptor - add auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    assertSecureApiTransport(config.baseURL || API_BASE_URL, window.location.origin, import.meta.env.DEV);
    const requestTarget = new URL(config.url || '', new URL(config.baseURL || API_BASE_URL, window.location.origin));
    assertSecureApiTransport(requestTarget.href, window.location.origin, import.meta.env.DEV);
    const state = store.getState();
    const token = state.auth.accessToken;
    const role = (state.auth.user?.role || '').toString().toUpperCase();
    const requestUrl = (config.url || '').toString();

    if (
      role === UserRole.WORKER &&
      (requestUrl.includes('/fleet/dashboard') || requestUrl.includes('/fleet/departments'))
    ) {
      return Promise.reject(new Error('Worker role is not authorized for supervisor fleet endpoints'));
    }
    
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (API_KEY && config.headers) {
      config.headers['X-API-Key'] = API_KEY;
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    
    // Handle 401 - try refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const state = store.getState();
      const refreshToken = state.auth.refreshToken;
      
      if (refreshToken) {
        try {
          assertSecureApiTransport(API_BASE_URL, window.location.origin, import.meta.env.DEV);
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });
          
          const { access_token, refresh_token } = response.data;
          store.dispatch(setTokens({ accessToken: access_token, refreshToken: refresh_token }));
          
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${access_token}`;
          }
          
          return apiClient(originalRequest);
        } catch {
          store.dispatch(logout());
        }
      } else {
        store.dispatch(logout());
      }
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;

// =============================================================
// AUTH API
// =============================================================

export const authApi = {
  login: async (email: string, password: string) => {
    const response = await apiClient.post('/auth/login', { email, password });
    return {
      ...response.data,
      user: normalizeUser(response.data.user),
    };
  },
  
  logout: async () => {
    await apiClient.post('/auth/logout');
  },
  
  getMe: async () => {
    const response = await apiClient.get('/auth/me');
    return normalizeUser(response.data);
  },
  
  refresh: async (refreshToken: string) => {
    const response = await apiClient.post('/auth/refresh', { refresh_token: refreshToken });
    return response.data;
  },
};

// =============================================================
// FLEET API
// =============================================================

export const fleetApi = {
  getSummary: async () => {
    const response = await apiClient.get('/fleet/summary');
    return normalizeFleetSummary(response.data);
  },
  
  getDashboard: async () => {
    const response = await apiClient.get('/fleet/dashboard');
    return normalizeFleetDashboard(response.data);
  },

  getCredentialOps: async () => {
    const response = await apiClient.get('/fleet/credential-ops');
    return normalizeCredentialOps(response.data);
  },

  getPilotReadiness: async () => {
    const response = await apiClient.get('/fleet/pilot-readiness');
    return normalizePilotReadiness(response.data);
  },
  
  getWorkers: async (params?: { onShiftOnly?: boolean; classification?: string; department?: string }) => {
    const response = await apiClient.get('/fleet/workers', {
      params: {
        on_shift_only: params?.onShiftOnly,
        classification: params?.classification,
        department: params?.department,
      },
    });
    return (response.data || []).map(normalizeFleetWorkerStatus);
  },
  
  getDepartments: async () => {
    const response = await apiClient.get('/fleet/departments');
    return (response.data || []).map(normalizeDepartment);
  },
  
  getAlerts: async (params?: { acknowledged?: boolean; limit?: number }) => {
    const response = await apiClient.get('/fleet/alerts', { params });
    return (response.data || []).map(normalizeAlert);
  },
  
  acknowledgeAlert: async (alertId: string) => {
    const response = await apiClient.post(`/fleet/alerts/${alertId}/acknowledge`);
    return response.data;
  },
  
  resolveAlert: async (alertId: string) => {
    const response = await apiClient.post(`/fleet/alerts/${alertId}/resolve`);
    return response.data;
  },
};

// =============================================================
// WEBHOOKS API
// =============================================================

export const webhooksApi = {
  getConfig: async () => {
    const response = await apiClient.get('/webhooks/config');
    return {
      url: response.data.url ?? null,
      secretConfigured: Boolean(response.data.secret_configured ?? response.data.secretConfigured),
      events: response.data.events ?? [],
    };
  },

  updateConfig: async (data: { url: string; secret?: string; events: string[] }) => {
    const response = await apiClient.put('/webhooks/config', data);
    return response.data;
  },

  test: async () => {
    const response = await apiClient.post('/webhooks/test');
    return response.data;
  },
};

// =============================================================
// WORKERS API
// =============================================================

export const workersApi = {
  list: async (params?: { skip?: number; limit?: number; isActive?: boolean; onShift?: boolean }) => {
    const response = await apiClient.get('/workers', { params });
    const raw = response.data;
    const items = raw?.items || raw?.workers || raw || [];
    return items.map(normalizeWorker);
  },
  
  get: async (workerId: string) => {
    const response = await apiClient.get(`/workers/${workerId}`);
    return normalizeWorker(response.data);
  },
  
  create: async (data: { workerCode: string; department?: string; jobTitle?: string }) => {
    const response = await apiClient.post('/workers', data);
    return response.data;
  },
  
  update: async (workerId: string, data: Partial<{ department: string; jobTitle: string; isActive: boolean }>) => {
    const response = await apiClient.patch(`/workers/${workerId}`, data);
    return response.data;
  },
  
  getAlerts: async (workerId: string, params?: { acknowledged?: boolean; limit?: number }) => {
    const response = await apiClient.get(`/workers/${workerId}/alerts`, { params });
    return (response.data || []).map(normalizeAlert);
  },
  
  startShift: async (workerId: string) => {
    const response = await apiClient.post(`/workers/${workerId}/shift/start`);
    return response.data;
  },
  
  endShift: async (workerId: string) => {
    const response = await apiClient.post(`/workers/${workerId}/shift/end`);
    return response.data;
  },
};

// =============================================================
// BIOMETRICS API
// =============================================================

export const biometricsApi = {
  getCurrentCari: async (workerId: string) => {
    const response = await apiClient.get(`/biometrics/cari/${workerId}`);
    return normalizeCARIScore(response.data);
  },
  
  getCariHistory: async (workerId: string, hours = 24) => {
    const response = await apiClient.get(`/biometrics/cari/${workerId}/history`, {
      params: { hours },
    });
    return normalizeCARIHistory(response.data);
  },
};
